import React, { useState, useMemo, useCallback } from "react";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { ALL_ROLE_LEVELS, ROLE_LEVEL_LABELS, normalizeRoleLevel } from "utils/roleUtils";
import { getSidebarPermissionItems } from "routeConfig";
import { PERMISSION_OVERRIDE_KEY } from "context/UserContext";
import { useUserContext } from "context/UserContext";

// 角色顯示顏色
const ROLE_COLORS = {
  1: "#2e7d32",
  2: "#1565c0",
  3: "#283593",
  4: "#e65100",
  5: "#00695c",
};

const DISPLAY_ROLE_LEVELS = [1, 2, 3, 4, 5];
const READABLE_TEXT_COLOR = "#344767";
const READABLE_MUTED_TEXT_COLOR = "#5B6B88";

const SIDENAV_PERMISSION_ITEMS = getSidebarPermissionItems();

const getLocalOverrides = () => {
  try {
    return JSON.parse(localStorage.getItem(PERMISSION_OVERRIDE_KEY) || "{}");
  } catch {
    return {};
  }
};

const saveLocalOverrides = (overrides) => {
  localStorage.setItem(PERMISSION_OVERRIDE_KEY, JSON.stringify(overrides));
};

const roleChip = (roleLevel) => (
  <Chip
    label={`lv${roleLevel}`}
    size="small"
    sx={{
      backgroundColor: ROLE_COLORS[roleLevel] || "#78909c",
      color: "#fff",
      fontWeight: 700,
      fontSize: "0.75rem",
    }}
  />
);

function PermissionTable() {
  const { roleLevel } = useUserContext() || {};
  const actorRank = normalizeRoleLevel(roleLevel);
  const visibleRoles = DISPLAY_ROLE_LEVELS.filter((displayRole) => {
    if (!actorRank) return true;
    return displayRole >= actorRank;
  });
  const matrixColumns = `minmax(280px, 2.2fr) repeat(${visibleRoles.length}, minmax(140px, 1fr))`;

  return (
    <Paper sx={{ overflowX: "auto", borderRadius: 3, boxShadow: "0 10px 24px rgba(0,0,0,0.08)" }}>
      <MDBox sx={{ minWidth: 920 }}>
        <MDBox
          sx={{
            display: "grid",
            gridTemplateColumns: matrixColumns,
            alignItems: "center",
            backgroundColor: "#f7f8fa",
            borderBottom: "1px solid #eceff4",
          }}
        >
          <MDBox sx={{ px: 4, py: 3.5 }}>
            <MDTypography variant="h5" fontWeight="bold" color="dark">
              頁面
            </MDTypography>
          </MDBox>
          {visibleRoles.map((role) => (
            <MDBox
              key={role}
              sx={{
                px: 2,
                py: 3,
                minHeight: 96,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <MDTypography
                variant="caption"
                sx={{
                  color: READABLE_TEXT_COLOR,
                  fontWeight: 600,
                  lineHeight: 1.35,
                  maxWidth: 120,
                  fontSize: "0.95rem",
                }}
              >
                {`lv${role} ${ROLE_LEVEL_LABELS[role] || ""}`.trim()}
              </MDTypography>
            </MDBox>
          ))}
        </MDBox>

        {SIDENAV_PERMISSION_ITEMS.map((page, idx) => (
          <MDBox
            key={page.key}
            sx={{
              display: "grid",
              gridTemplateColumns: matrixColumns,
              alignItems: "center",
              backgroundColor: idx % 2 === 0 ? "#fcfcfd" : "#fff",
              borderBottom:
                idx === SIDENAV_PERMISSION_ITEMS.length - 1 ? "none" : "1px solid #f0f2f5",
            }}
          >
            <MDBox sx={{ px: 4, py: 3.5 }}>
              <MDTypography variant="h5" fontWeight="bold" color="dark">
                {page.label}
              </MDTypography>
            </MDBox>

            {visibleRoles.map((role) => {
              const hasAccess = page.roleLevels.includes(role);
              return (
                <MDBox
                  key={role}
                  sx={{
                    px: 2,
                    py: 3.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {hasAccess ? (
                    <CheckIcon sx={{ color: "#2e7d32", fontSize: 22 }} />
                  ) : (
                    <CloseIcon sx={{ color: "#d9dee7", fontSize: 22 }} />
                  )}
                </MDBox>
              );
            })}
          </MDBox>
        ))}
      </MDBox>
    </Paper>
  );
}

function AccountOverrideManager() {
  const [overrides, setOverrides] = useState(getLocalOverrides);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null);
  const [formEmail, setFormEmail] = useState("");
  const [formRole, setFormRole] = useState("3");
  const [saved, setSaved] = useState(false);

  const overrideList = useMemo(
    () =>
      Object.entries(overrides).map(([email, roleLevel]) => ({
        email,
        roleLevel: normalizeRoleLevel(roleLevel),
      })),
    [overrides]
  );

  const openAdd = useCallback(() => {
    setEditingEmail(null);
    setFormEmail("");
    setFormRole("3");
    setDialogOpen(true);
  }, []);

  const openEdit = useCallback((email, roleLevel) => {
    setEditingEmail(email);
    setFormEmail(email);
    setFormRole(String(roleLevel));
    setDialogOpen(true);
  }, []);

  const handleSave = useCallback(() => {
    const email = formEmail.trim().toLowerCase();
    if (!email) return;
    const next = { ...overrides };
    if (editingEmail && editingEmail !== email) {
      delete next[editingEmail];
    }
    next[email] = normalizeRoleLevel(formRole);
    setOverrides(next);
    saveLocalOverrides(next);
    setDialogOpen(false);
    setSaved(true);
  }, [editingEmail, formEmail, formRole, overrides]);

  const handleDelete = useCallback(
    (email) => {
      const next = { ...overrides };
      delete next[email];
      setOverrides(next);
      saveLocalOverrides(next);
      setSaved(true);
    },
    [overrides]
  );

  const handleApply = useCallback(() => {
    sessionStorage.clear();
    window.location.reload();
  }, []);

  return (
    <MDBox>
      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        <MDTypography variant="h6">帳號角色覆蓋設定</MDTypography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="small"
          sx={{
            backgroundColor: "#2f5be7",
            color: "#fff",
            "&:hover": { backgroundColor: "#2347c4" },
          }}
          onClick={openAdd}
        >
          新增覆蓋
        </Button>
        {saved && (
          <Button variant="contained" size="small" color="success" onClick={handleApply}>
            套用並重新整理
          </Button>
        )}
      </Stack>

      {saved && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          已儲存變更。點擊「套用並重新整理」讓所有帳號在下次登入時套用新設定。
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>說明：</strong>此設定會覆蓋帳號從 CRM 取得的角色，讓指定帳號看到對應角色的畫面。
        變更後需重新登入才會生效。此設定儲存於本瀏覽器的 localStorage，僅對此瀏覽器有效。
      </Alert>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">
                套用角色模板
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">
                頁面差異說明
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">
                操作
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {overrideList.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={4}
                  align="center"
                  sx={{ color: READABLE_MUTED_TEXT_COLOR, py: 4 }}
                >
                  尚無覆蓋設定
                </TableCell>
              </TableRow>
            )}
            {overrideList.map(({ email, roleLevel }) => (
              <TableRow key={email}>
                <TableCell>{email}</TableCell>
                <TableCell align="center">{roleChip(roleLevel)}</TableCell>
                <TableCell align="center">
                  <MDTypography variant="caption" sx={{ color: READABLE_TEXT_COLOR }}>
                    {`lv${roleLevel} ${ROLE_LEVEL_LABELS[roleLevel] || ""}`.trim()}
                  </MDTypography>
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Tooltip title="編輯">
                      <IconButton size="small" onClick={() => openEdit(email, roleLevel)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="刪除">
                      <IconButton size="small" color="error" onClick={() => handleDelete(email)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editingEmail ? "編輯角色覆蓋" : "新增角色覆蓋"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={0.5}>
            <TextField
              label="Email"
              fullWidth
              size="small"
              value={formEmail}
              disabled={Boolean(editingEmail)}
              onChange={(e) => setFormEmail(e.target.value)}
            />
            <TextField
              select
              label="套用角色模板"
              fullWidth
              size="small"
              value={formRole}
              onChange={(e) => setFormRole(e.target.value)}
            >
              {ALL_ROLE_LEVELS.map((level) => (
                <MenuItem key={level} value={String(level)}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {roleChip(level)}
                    <span>{`lv${level} ${ROLE_LEVEL_LABELS[level] || ""}`.trim()}</span>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
            <Alert severity="info" sx={{ fontSize: "0.8rem" }}>
              <strong>
                {`套用 lv${formRole} ${
                  ROLE_LEVEL_LABELS[normalizeRoleLevel(formRole)] || ""
                } 模板後，該帳號可見功能：`}
              </strong>
              <br />
              {SIDENAV_PERMISSION_ITEMS.filter((page) =>
                page.roleLevels.includes(normalizeRoleLevel(formRole))
              )
                .map((page) => page.label)
                .join("、")}
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>取消</Button>
          <Button
            variant="contained"
            sx={{ color: "#fff" }}
            onClick={handleSave}
            disabled={!formEmail.trim()}
          >
            儲存
          </Button>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}

function PermissionManagement() {
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  權限管理
                </MDTypography>
              </MDBox>

              <MDBox px={3} py={2}>
                <MDBox>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <strong>說明：</strong>✓ 表示該角色可看到此功能頁面；✗ 表示無法訪問。
                  </Alert>
                  <PermissionTable />
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default PermissionManagement;
